<template>
  <div class="x" data-test="x" :dir="documentDirection">
    <SnippetConfigExtraParams :excluded-extra-params="excludedExtraParams" />
    <SnippetCallbacks />
    <Tagging :consent="true" />
    <UrlHandler />
    <MainModal v-if="isOpen" data-wysiwyg="layer" />
  </div>
</template>

<script lang="ts">
import type { SnippetConfig, XEvent } from '@empathyco/x-components'
import type { QueryPreviewInfo } from '@empathyco/x-components/queries-preview'
import type { ComputedRef } from 'vue'
import { SnippetCallbacks, use$x, useGetter, XPlugin } from '@empathyco/x-components'
import { SnippetConfigExtraParams } from '@empathyco/x-components/extra-params'
import { Tagging } from '@empathyco/x-components/tagging'
import { UrlHandler } from '@empathyco/x-components/url'
import { zip } from 'rxjs'
import {
  computed,
  defineAsyncComponent,
  defineComponent,
  getCurrentInstance,
  inject,
  onUnmounted,
  provide,
  ref,
  watch,
} from 'vue'
import { useDevice } from './composables/use-device.composable'
import currencies from './i18n/currencies'
import './tailwind/index.css'

export default defineComponent({
  components: {
    SnippetCallbacks,
    SnippetConfigExtraParams,
    Tagging,
    UrlHandler,
    MainModal: defineAsyncComponent(() =>
      import('./components/custom-main-modal.vue').then(m => m.default),
    ),
  },
  setup() {
    const x = use$x()
    const appInstance = getCurrentInstance()
    const { deviceName } = useDevice()
    const { request: searchRequest } = useGetter('search')

    const snippetConfig = inject<SnippetConfig>('snippetConfig')!
    const isOpen = ref(false)
    const isVoiceListening = ref(false)

    const excludedExtraParams = [
      'callbacks',
      'uiLang',
      'consent',
      'documentDirection',
      'currency',
      'filters',
      'isolate',
      'queriesPreview',
      'searchPositionSelector',
      'wishlist',
    ]

    const openXEvents = ['UserOpenXProgrammatically', 'UserClickedOpenX']

    const open = (): void => {
      isOpen.value = true
    }

    openXEvents.forEach(event => x.on(event as XEvent, false).subscribe(open))

    x.on('UserClickedCloseX', false).subscribe(() => {
      close()
      isVoiceListening.value = false
    })

    x.on('ElementSwipedDown', false).subscribe((elementId): void => {
      switch (elementId) {
        case 'mobile-main':
          // Reload page when swiping down on mobile
          window.location.reload()
          break
        case 'mobile-aside':
          x.emit('UserClickedCloseModal', 'aside-modal')
          break
      }
    })

    x.on('UserClickedOpenModal', false).subscribe(payload => {
      if (payload === 'aside-modal' || payload === 'right-aside') {
        x.emit('UserOpenedFacetsAside', searchRequest.value)
      }
    })

    const documentDirection = computed(() => {
      return (
        document.documentElement.dir ||
        document.body.dir ||
        (snippetConfig.documentDirection ?? 'ltr')
      )
    })

    const currencyFormat = computed(() => currencies[snippetConfig.currency!])
    provide<string>('currencyFormat', currencyFormat.value)

    const queriesPreviewInfo = computed(() => snippetConfig.queriesPreview ?? [])
    provide<ComputedRef<QueryPreviewInfo[]> | undefined>('queriesPreviewInfo', queriesPreviewInfo)

    const zipSubscription = zip([
      XPlugin.bus.on('SemanticQueriesResponseChanged'),
      XPlugin.bus.on('RelatedPromptsResponseChanged'),
    ]).subscribe(response => {
      x.emit('FallbackNoResultsResponseChanged' as XEvent, response.flat() as any[])
    })

    watch(
      () => snippetConfig.uiLang,
      uiLang => appInstance?.appContext.config.globalProperties.$setLocale(uiLang ?? 'en'),
    )

    watch(deviceName, device =>
      appInstance?.appContext.config.globalProperties.$setLocaleDevice(device),
    )

    // We are commenting this code because the customer wants to see the predictive layer without the keyboard.
    //fix keyboard issue on iOS
    /*if (isIOS()) {
      useEventListener(document, 'touchmove', removeSearchInputFocus)
    }*/

    x.on('VoiceRecognitionListeningChanged', true).subscribe(payload => {
      isVoiceListening.value = payload.eventPayload
    })
    provide('isVoiceListening', isVoiceListening)

    onUnmounted(() => {
      zipSubscription.unsubscribe()
    })

    return {
      isOpen,
      documentDirection,
      excludedExtraParams,
    }
  },
})
</script>

<style scoped>
.x-modal:deep(.x-modal__content) {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow: auto;
}
.x-modal:deep(.x-modal__content):has(.x-base-grid),
.x-modal:deep(.x-modal__content):has(.x-empathize:not([style*='display: none'])) {
  overflow: hidden;
}
</style>

<style lang="scss">
*:not(.x-keyboard-navigation *) {
  outline: none;
}

.x-banner,
.x-promoted {
  &__title {
    display: none;
  }
}
</style>
